import { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Landmark,
  DollarSign,
  TrendingDown,
  TrendingUp,
  Camera,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import useStore from '../../store/useStore';
import { Card, KPICard } from '../../components/ui/Card';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatCurrency, formatPercent } from '../../utils/format';
import {
  computeSubtotals,
  formatMonthKey,
  formatMonthKeyLong,
  formatMonthKeyShort,
} from '../../data/balanceSheetHistory';
import { inferBalanceSheetCategory } from '../../data/balanceSheetCategories';
import { buildMonthSnapshot } from '../../utils/buildMonthSnapshot';

// ─── Constants ──────────────────────────────────────────────────────────────
const TAX_ADV_KEYWORDS = ['401k', '401(k)', 'ira', 'hsa', 'roth', 'sep', '403b', '457'];

function isTaxAdvantage(name) {
  const lower = (name || '').toLowerCase();
  return TAX_ADV_KEYWORDS.some((k) => lower.includes(k));
}

// ─── ROW DEFINITIONS ────────────────────────────────────────────────────────
// type: section | category | line | subtotal | metric | total | spacer
const ROW_DEFS = [
  // ── ASSETS ──
  { id: 'hdr-assets', type: 'section', label: 'ASSETS' },
  { id: 'cat-cash', type: 'category', label: 'Cash & Cash Equivalents' },
  { id: 'cashOnHand', type: 'line', label: 'Cash on Hand', historyKey: 'cashOnHand' },
  { id: 'checking', type: 'line', label: 'Checking Accounts', historyKey: 'checking' },
  { id: 'savings', type: 'line', label: 'Savings Accounts', historyKey: 'savings' },
  { id: 'tBills', type: 'line', label: 'T-Bills', historyKey: 'tBills' },
  { id: 'sub-cash', type: 'subtotal', label: 'Cash & Cash Equivalents', historyKey: 'cashTotal' },
  { id: 'met-cashPct', type: 'metric', label: '% of Total Assets', computed: 'pctOfAssets', sourceKey: 'cashTotal' },
  { id: 'sp1', type: 'spacer' },

  { id: 'cat-invest', type: 'category', label: 'Investments' },
  { id: 'robinhood', type: 'line', label: 'Robinhood', historyKey: 'robinhood' },
  { id: 'fundrise', type: 'line', label: 'Fundrise', historyKey: 'fundrise' },
  { id: 'sub-brokerage', type: 'subtotal', label: 'Brokerage Accounts', historyKey: 'brokerageTotal', indent: 2 },
  { id: 'roth401k', type: 'line', label: 'Roth 401K', historyKey: 'roth401k' },
  { id: 'traditionalIra', type: 'line', label: 'Traditional IRA', historyKey: 'traditionalIra' },
  { id: 'rothIra', type: 'line', label: 'IRA', historyKey: 'rothIra' },
  { id: 'hsa', type: 'line', label: 'HSA', historyKey: 'hsa' },
  { id: 'sub-taxadv', type: 'subtotal', label: 'Tax Advantage Accounts', historyKey: 'taxAdvTotal', indent: 2 },
  { id: 'sub-investments', type: 'subtotal', label: 'Investments Accounts', historyKey: 'investmentsTotal' },
  { id: 'met-investMom', type: 'metric', label: 'MoM $ Change', computed: 'momDollar', sourceKey: 'investmentsTotal', format: 'currency', colorCode: true },
  { id: 'met-investMomPct', type: 'metric', label: 'MoM % Change', computed: 'momPct', sourceKey: 'investmentsTotal', format: 'percent', colorCode: true },
  { id: 'sp2', type: 'spacer' },

  { id: 'cat-property', type: 'category', label: 'Property' },
  { id: 'primaryResidence', type: 'line', label: 'Primary Residence', historyKey: '_zero' },
  { id: 'margin', type: 'line', label: 'Margin', historyKey: '_zero' },
  { id: 'rentalProperty', type: 'line', label: 'Rental Property', historyKey: '_zero' },
  { id: 'purchasedWebsite', type: 'line', label: 'Purchased Website', historyKey: '_zero' },
  { id: 'vehicle', type: 'line', label: 'Vehicle', historyKey: 'vehicle' },
  { id: 'sub-property', type: 'subtotal', label: 'Property', historyKey: 'propertyTotal' },
  { id: 'sp3', type: 'spacer' },

  { id: 'cat-other', type: 'category', label: 'Other Assets' },
  { id: 'lifeInsurance', type: 'line', label: 'Life Insurance', historyKey: 'lifeInsurance' },
  { id: 'loyaltyPoints', type: 'line', label: 'Est. Loyalty Award Points Value', historyKey: 'loyaltyPoints' },
  { id: 'accountsReceivable', type: 'line', label: 'Accounts Receivable', historyKey: 'accountsReceivable' },
  { id: 'securityDeposit', type: 'line', label: 'Security Deposit', historyKey: 'securityDeposit' },
  { id: 'ownershiftEquity', type: 'line', label: 'Private Equity', historyKey: 'ownershiftEquity' },
  { id: 'collectables', type: 'line', label: 'Collectables', historyKey: 'collectables' },
  { id: 'sub-other', type: 'subtotal', label: 'Other Assets', historyKey: 'otherAssetsTotal' },
  { id: 'sp4', type: 'spacer' },

  { id: 'total-assets', type: 'total', label: 'Total Assets', historyKey: 'totalAssets' },
  { id: 'met-assetsMom', type: 'metric', label: 'MoM $ Change', computed: 'momDollar', sourceKey: 'totalAssets', format: 'currency', colorCode: true },
  { id: 'met-assetsMomPct', type: 'metric', label: 'MoM % Change', computed: 'momPct', sourceKey: 'totalAssets', format: 'percent', colorCode: true },
  { id: 'sp5', type: 'spacer' },

  // ── LIABILITIES ──
  { id: 'hdr-liab', type: 'section', label: 'LIABILITIES' },
  { id: 'cat-credit', type: 'category', label: 'Short-Term Credit' },
  { id: 'creditCards', type: 'line', label: 'Credit Cards', historyKey: 'creditCards' },
  { id: 'loansFromFriends', type: 'line', label: 'Accounts Payable', historyKey: 'loansFromFriends' },
  { id: 'sub-credit', type: 'subtotal', label: 'Short-Term Credit', historyKey: 'ccTotal' },
  { id: 'sp-credit', type: 'spacer' },
  { id: 'cat-loans', type: 'category', label: 'Loans & Mortgages' },
  { id: 'loanPrimaryRes', type: 'line', label: 'Primary Residence', historyKey: '_zero' },
  { id: 'loanRental', type: 'line', label: 'Rental Property', historyKey: '_zero' },
  { id: 'vehicleLoan', type: 'line', label: 'Vehicle Payable', historyKey: 'vehicleLoan' },
  { id: 'businessLoans', type: 'line', label: 'Business Loans', historyKey: '_zero' },
  { id: 'rhMarginLoan', type: 'line', label: 'RH Margin Loan', historyKey: '_zero' },
  { id: 'lifeInsLoan', type: 'line', label: 'Life Insurance Loan', historyKey: '_zero' },
  { id: '401kLoan', type: 'line', label: '401k loan', historyKey: '_zero' },
  { id: 'sub-loans', type: 'subtotal', label: 'Loans & Mortgages', historyKey: 'loansTotal' },
  { id: 'sp-loans', type: 'spacer' },
  { id: 'total-liab', type: 'total', label: 'Total Liabilities', historyKey: 'totalLiabilities' },
  { id: 'sp7', type: 'spacer' },

  // ── EQUITY ──
  { id: 'hdr-equity', type: 'section', label: 'EQUITY' },
  { id: 'total-nw', type: 'total', label: 'Current Net Worth', historyKey: 'netWorth', highlight: true },
  { id: 'met-nwMom', type: 'metric', label: 'MoM $ Change', computed: 'momDollar', sourceKey: 'netWorth', format: 'currency', colorCode: true },
  { id: 'met-nwMomPct', type: 'metric', label: 'MoM % Change', computed: 'momPct', sourceKey: 'netWorth', format: 'percent', colorCode: true },
  { id: 'sp8', type: 'spacer' },

  // ── CONTRIBUTIONS ──
  { id: 'hdr-contrib', type: 'section', label: 'CONTRIBUTIONS' },
  { id: 'contribRothIra', type: 'line', label: 'IRA', historyKey: 'contribRothIra' },
  { id: 'contribHsa', type: 'line', label: 'HSA', historyKey: 'contribHsa' },
  { id: 'contrib401k', type: 'line', label: '401(k)', historyKey: 'contrib401k' },
  { id: 'contribBrokerage', type: 'line', label: 'Brokerage', historyKey: 'contribBrokerage' },
  { id: 'sub-contrib', type: 'subtotal', label: 'Total Contributions', historyKey: 'contribTotal' },
  { id: 'sp-contrib', type: 'spacer' },

  // ── METRICS ──
  { id: 'hdr-metrics', type: 'section', label: 'METRICS' },
  { id: 'creditScore', type: 'line', label: 'Credit Score', historyKey: 'creditScore', format: 'integer' },
  { id: 'met-csMom', type: 'metric', label: 'MoM Change', computed: 'momDollar', sourceKey: 'creditScore', format: 'integer', colorCode: true },
];

// ─── Cell Value Resolution ──────────────────────────────────────────────────

function resolveCellValue(row, monthData, prevMonthData) {
  if (row.type === 'section' || row.type === 'category' || row.type === 'spacer') return null;

  if (row.computed === 'pctOfAssets') {
    const val = monthData[row.sourceKey] || 0;
    const total = monthData.totalAssets || 0;
    return total > 0 ? (val / total) * 100 : 0;
  }

  if (row.computed === 'momDollar') {
    if (!prevMonthData) return null;
    const curr = monthData[row.sourceKey] || 0;
    const prev = prevMonthData[row.sourceKey] || 0;
    return curr - prev;
  }

  if (row.computed === 'momPct') {
    if (!prevMonthData) return null;
    const curr = monthData[row.sourceKey] || 0;
    const prev = prevMonthData[row.sourceKey] || 0;
    if (Math.abs(prev) < 0.01) return null;
    return ((curr - prev) / Math.abs(prev)) * 100;
  }

  if (row.historyKey === '_zero') return 0;

  if (row.historyKey && monthData) {
    return monthData[row.historyKey] ?? 0;
  }

  return 0;
}

function formatCellValue(value, row) {
  if (value == null) return '—';

  if (row.computed === 'pctOfAssets' || row.format === 'percent') {
    return `${value >= 0 ? '' : '-'}${Math.abs(value).toFixed(1)}%`;
  }

  if (row.format === 'integer') {
    if (value === 0) return '—';
    const sign = row.colorCode && value > 0 ? '+' : '';
    return `${sign}${Math.round(value).toLocaleString()}`;
  }

  if (row.format === 'currency' || row.computed === 'momDollar') {
    if (Math.abs(value) < 0.005) return '—';
    return formatBS(value);
  }

  // Default: currency for line/subtotal/total
  if (row.type === 'line' && Math.abs(value) < 0.005) return '-';
  return formatBS(value);
}

/** Format a value in BS style: negatives in parentheses, commas, no decimals */
function formatBS(value) {
  if (value == null || isNaN(value)) return '-';
  if (Math.abs(value) < 0.005) return '-';
  const abs = Math.abs(value);
  const formatted = '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return value < 0 ? `(${formatted})` : formatted;
}

// ─── Build live month data from store to match getMonthData() shape ─────────
function buildLiveMonthData(liveBS, creditScore = 0, currentMonthBS = {}, accounts = []) {
  // Map live BS data to the same keys as getMonthData() using balanceSheetCategory.
  // For non-investment accounts: sum by explicit category (or inferred fallback).
  // For investment accounts with holdings: match holding account names to account
  // records to pick up their category, falling back to name-based inference.
  const sums = {
    cashOnHand: 0, checking: 0, savings: 0, tBills: 0,
    robinhood: 0, fundrise: 0,
    roth401k: 0, traditionalIra: 0, rothIra: 0, hsa: 0,
    vehicle: 0,
    lifeInsurance: 0, loyaltyPoints: 0, accountsReceivable: 0,
    securityDeposit: 0, ownershiftEquity: 0, collectables: 0,
    loansFromFriends: 0, vehicleLoan: 0,
  };

  // ── Non-investment accounts: use liveBS item arrays (which already have
  //    computed overrides for loyalty points, ownershift equity, etc.) ──
  const accountArrays = [
    liveBS.cashItems,
    liveBS.propertyItems,
    liveBS.otherAssetItems,
  ];
  accountArrays.forEach((items) => {
    (items || []).forEach((a) => {
      const cat = a.balanceSheetCategory || inferBalanceSheetCategory(a);
      if (cat && cat in sums) sums[cat] += (a.balance || 0);
    });
  });

  // Loan accounts (liveBS stores them with abs(balance))
  (liveBS.loanItems || []).forEach((a) => {
    const cat = a.balanceSheetCategory || inferBalanceSheetCategory(a);
    if (cat && cat in sums) sums[cat] += (a.balance || 0);
  });

  // ── Investment accounts with holdings: map holding account names → category ──
  // Build a lookup from account name (lowercase) → balanceSheetCategory
  const acctCategoryByName = {};
  accounts.forEach((a) => {
    if (a.type === 'investment') {
      const cat = a.balanceSheetCategory || inferBalanceSheetCategory(a);
      if (cat) acctCategoryByName[(a.name || '').toLowerCase()] = cat;
    }
  });

  // Helper: find category for a holding account name
  const findInvestCat = (holdingAcctName) => {
    const lower = (holdingAcctName || '').toLowerCase();
    // Exact match first
    if (acctCategoryByName[lower]) return acctCategoryByName[lower];
    // Partial match (holding name might be shortened, e.g. "Robinhood Brokerage" vs "Robinhood Brokerage 0937")
    for (const [name, cat] of Object.entries(acctCategoryByName)) {
      if (lower.includes(name) || name.includes(lower)) return cat;
    }
    // Fallback: infer from name
    return inferBalanceSheetCategory({ type: 'investment', name: holdingAcctName });
  };

  // Brokerage + tax-advantage holding accounts
  [...(liveBS.brokerageAccts || []), ...(liveBS.taxAdvantageAccts || [])].forEach(([name, data]) => {
    const cat = findInvestCat(name);
    if (cat && cat in sums) sums[cat] += (data.total || 0);
  });

  // Investment accounts WITHOUT holdings (already filtered in liveBS.investAcctEntries)
  (liveBS.investAcctEntries || []).forEach((a) => {
    const cat = a.balanceSheetCategory || inferBalanceSheetCategory(a);
    if (cat && cat in sums) sums[cat] += (a.balance || 0);
  });

  // Credit cards — always from liveBS
  const creditCardsVal = liveBS.ccTotal;

  // Contributions from the current month's BS record
  const contribRothIra = currentMonthBS.contribRothIra || 0;
  const contribHsa = currentMonthBS.contribHsa || 0;
  const contrib401k = currentMonthBS.contrib401k || 0;
  const contribBrokerage = currentMonthBS.contribBrokerage || 0;
  const contribTotal = contribRothIra + contribHsa + contrib401k + contribBrokerage;

  return {
    ...sums,
    creditCards: creditCardsVal,
    contribRothIra,
    contribHsa,
    contrib401k,
    contribBrokerage,
    contribTotal,
    cashTotal: liveBS.cashTotal,
    brokerageTotal: liveBS.brokerageTotal,
    taxAdvTotal: liveBS.taxAdvTotal,
    investmentsTotal: liveBS.investmentsTotal,
    propertyTotal: liveBS.propertyTotal,
    otherAssetsTotal: liveBS.otherAssetsTotal,
    totalAssets: liveBS.totalAssets,
    ccTotal: liveBS.ccTotal,
    loansTotal: liveBS.loansTotal,
    totalLiabilities: liveBS.totalLiabilities,
    netWorth: liveBS.netWorth,
    creditScore,
    _zero: 0,
  };
}

// ─── Row Style Map ──────────────────────────────────────────────────────────
const ROW_STYLES = {
  section: {
    label: 'py-2 px-3 text-[11px] font-bold uppercase tracking-widest text-accent bg-accent/8',
    cell: 'py-2 px-2 bg-accent/8',
  },
  category: {
    label: 'pt-2 pb-1 pl-4 pr-3 text-xs font-semibold text-text-primary',
    cell: 'pt-2 pb-1 px-2',
  },
  line: {
    label: 'py-0.5 pl-8 pr-3 text-xs text-text-secondary',
    cell: 'py-0.5 px-2 text-xs text-text-primary text-right font-mono tabular-nums whitespace-nowrap',
  },
  subtotal: {
    label: 'py-1 pl-4 pr-3 text-xs font-semibold text-text-primary border-t border-border/30',
    cell: 'py-1 px-2 text-xs font-semibold text-text-primary text-right font-mono tabular-nums whitespace-nowrap border-t border-border/30',
  },
  metric: {
    label: 'py-0.5 pl-8 pr-3 text-[11px] text-text-muted italic',
    cell: 'py-0.5 px-2 text-[11px] text-right font-mono italic whitespace-nowrap',
  },
  total: {
    label: 'py-2 px-3 text-xs font-bold text-text-primary border-t-2 border-border',
    cell: 'py-2 px-2 text-xs font-bold text-text-primary text-right font-mono tabular-nums whitespace-nowrap border-t-2 border-border bg-bg-tertiary/30',
  },
  spacer: {
    label: 'py-1',
    cell: 'py-1',
  },
};

// ─── Chart Tooltip ──────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-text-secondary text-xs mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="text-sm" style={{ color: entry.stroke }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
}

// ─── BSRow Component ────────────────────────────────────────────────────────
function BSRow({ row, columns, allMonthData }) {
  const styles = ROW_STYLES[row.type];
  if (!styles) return null;

  // Hide line rows that have no values across all displayed columns
  if (row.type === 'line' && row.historyKey) {
    const hasAnyValue = columns.some((col) => {
      const md = allMonthData[col.key];
      if (!md) return false;
      const val = row.historyKey === '_zero' ? 0 : (md[row.historyKey] ?? 0);
      return Math.abs(val) >= 0.01;
    });
    if (!hasAnyValue) return null;
  }

  // Sticky label cell: always opaque bg-bg-card so scrolled content can't bleed through
  const stickyLabel = `sticky left-0 z-10 whitespace-nowrap`;
  const stickyCss = { backgroundColor: 'var(--color-bg-card)' };

  if (row.type === 'spacer') {
    return (
      <tr>
        <td className={`${stickyLabel} ${styles.label}`} style={stickyCss} />
        {columns.map((col) => (
          <td key={col.key} className={col.isLive ? `${styles.cell} bg-accent/4` : styles.cell} />
        ))}
      </tr>
    );
  }

  if (row.type === 'section') {
    return (
      <tr>
        <td className={`${stickyLabel} ${styles.label}`} style={stickyCss}>{row.label}</td>
        {columns.map((col) => (
          <td key={col.key} className={col.isLive ? `${styles.cell} bg-accent/4` : styles.cell} />
        ))}
      </tr>
    );
  }

  if (row.type === 'category') {
    return (
      <tr>
        <td className={`${stickyLabel} ${styles.label}`} style={stickyCss}>{row.label}</td>
        {columns.map((col) => (
          <td key={col.key} className={col.isLive ? `${styles.cell} bg-accent/4` : styles.cell} />
        ))}
      </tr>
    );
  }

  // For subtotals with indent level 2
  const labelClass = row.indent === 2
    ? styles.label.replace('pl-4', 'pl-7')
    : styles.label;

  return (
    <tr className={row.type === 'line' ? 'hover:bg-bg-tertiary/20 transition-colors' : ''}>
      <td className={`${stickyLabel} ${labelClass}`} style={stickyCss}>
        {row.label}
      </td>
      {columns.map((col, colIdx) => {
        const monthData = allMonthData[col.key];
        const prevKey = colIdx > 0 ? columns[colIdx - 1].key : col.prevKey;
        const prevData = allMonthData[prevKey] || null;
        const value = resolveCellValue(row, monthData, prevData);
        const display = formatCellValue(value, row);

        // Color coding for metrics
        let colorClass = '';
        if (row.colorCode && typeof value === 'number' && value !== 0) {
          colorClass = value >= 0 ? 'text-success' : 'text-danger';
        }
        if (row.type === 'total' && row.highlight && typeof value === 'number') {
          colorClass = value >= 0 ? 'text-success' : 'text-danger';
        }

        const liveBg = col.isLive ? ' bg-accent/4' : '';

        return (
          <td key={col.key} className={`${styles.cell}${liveBg} ${colorClass}`}>
            {display}
          </td>
        );
      })}
    </tr>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function BalanceSheetMonthly() {
  // ── Store Data ──
  const accounts = useStore((s) => s.accounts);
  const creditCards = useStore((s) => s.creditCards);
  const investments = useStore((s) => s.investments);
  const netWorthHistory = useStore((s) => s.netWorthHistory);
  const budgetCategories = useStore((s) => s.budgetCategories);
  const addNetWorthSnapshot = useStore((s) => s.addNetWorthSnapshot);
  const balanceSheetMonthly = useStore((s) => s.balanceSheetMonthly);
  const upsertBalanceSheetMonth = useStore((s) => s.upsertBalanceSheetMonth);
  const userSettings = useStore((s) => s.userSettings);

  // Expanded = show all months from Aug 2024; collapsed = last 12 months
  const [expanded, setExpanded] = useState(false);

  // Current month key, e.g. '2026-03'
  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const currentMonthLabel = useMemo(() => formatMonthKeyLong(currentMonthKey), [currentMonthKey]);

  // ═══════════════════════════ LIVE BALANCE SHEET (Current Month) ═══════════
  const liveBS = useMemo(() => {
    // ── Cash & Cash Equivalents ──
    const cashItems = accounts
      .filter((a) => ['cash', 'checking', 'savings'].includes(a.type) && (a.balance || 0) !== 0)
      .sort((a, b) => (b.balance || 0) - (a.balance || 0));
    const cashTotal = cashItems.reduce((s, a) => s + (a.balance || 0), 0);

    // ── Investments ──
    const holdingsByAccount = {};
    investments.forEach((inv) => {
      const acct = inv.account || 'Unassigned';
      if (!holdingsByAccount[acct]) holdingsByAccount[acct] = { holdings: [], total: 0 };
      holdingsByAccount[acct].holdings.push(inv);
      holdingsByAccount[acct].total += inv.currentValue || 0;
    });

    const brokerageAccts = Object.entries(holdingsByAccount)
      .filter(([name, data]) => !isTaxAdvantage(name) && data.total !== 0)
      .sort(([, a], [, b]) => b.total - a.total);
    const taxAdvantageAccts = Object.entries(holdingsByAccount)
      .filter(([name, data]) => isTaxAdvantage(name) && data.total !== 0)
      .sort(([, a], [, b]) => b.total - a.total);

    const brokerageTotal = brokerageAccts.reduce((s, [, d]) => s + d.total, 0);
    const taxAdvTotal = taxAdvantageAccts.reduce((s, [, d]) => s + d.total, 0);

    const holdingAccountNames = Object.keys(holdingsByAccount).map((n) => n.toLowerCase());
    const investAcctEntries = accounts
      .filter((a) => a.type === 'investment' && (a.balance || 0) !== 0)
      .filter((a) => {
        const nameLower = (a.name || '').toLowerCase();
        return !holdingAccountNames.some(
          (h) => nameLower.includes(h) || h.includes(nameLower)
        );
      })
      .sort((a, b) => (b.balance || 0) - (a.balance || 0));
    const investAcctTotal = investAcctEntries.reduce((s, a) => s + (a.balance || 0), 0);
    const investmentsTotal = brokerageTotal + taxAdvTotal + investAcctTotal;

    // ── Property ──
    const propertyItems = accounts
      .filter((a) => a.type === 'property' && (a.balance || 0) !== 0)
      .sort((a, b) => (b.balance || 0) - (a.balance || 0));
    const propertyTotal = propertyItems.reduce((s, a) => s + (a.balance || 0), 0);

    // ── Other Assets ──
    const loyaltyPointsValue = creditCards.reduce((sum, c) => sum + (c.pointsBalance || 0), 0) * 0.01;
    const otherAssetItems = accounts
      .filter((a) => a.type === 'other')
      .map((a) => /est\.?\s*loyalty/i.test(a.name) ? { ...a, balance: loyaltyPointsValue } : a)
      .filter((a) => (a.balance || 0) !== 0)
      .sort((a, b) => (b.balance || 0) - (a.balance || 0));
    const otherAssetsTotal = otherAssetItems.reduce((s, a) => s + (a.balance || 0), 0);

    const totalAssets = cashTotal + investmentsTotal + propertyTotal + otherAssetsTotal;

    // ── Liabilities ──
    const ccItems = creditCards
      .filter((c) => c.balance !== 0)
      .map((c) => ({
        id: c.id,
        name: c.name || `Card •${c.lastFour || '????'}`,
        balance: Math.abs(c.balance || 0),
      }))
      .sort((a, b) => b.balance - a.balance);
    const ccTotal = ccItems.reduce((s, c) => s + c.balance, 0);

    const loanItems = accounts
      .filter((a) => a.type === 'loan' && (a.balance || 0) !== 0)
      .map((a) => ({ ...a, balance: Math.abs(a.balance || 0) }))
      .sort((a, b) => b.balance - a.balance);
    const loansTotal = loanItems.reduce((s, a) => s + a.balance, 0);

    const totalLiabilities = ccTotal + loansTotal;
    const netWorth = totalAssets - totalLiabilities;

    const liquidAssets = cashTotal + brokerageTotal;
    const liquidPct = totalAssets > 0 ? (liquidAssets / totalAssets) * 100 : 0;
    const cashPct = totalAssets > 0 ? (cashTotal / totalAssets) * 100 : 0;
    const investPct = totalAssets > 0 ? (investmentsTotal / totalAssets) * 100 : 0;

    const monthlySpend = budgetCategories.reduce((s, c) => s + (c.actualMonthly || 0), 0);

    return {
      cashItems, cashTotal, cashPct,
      brokerageAccts, brokerageTotal,
      taxAdvantageAccts, taxAdvTotal,
      investAcctEntries, investAcctTotal, investmentsTotal, investPct,
      propertyItems, propertyTotal,
      otherAssetItems, otherAssetsTotal,
      totalAssets,
      ccItems, ccTotal,
      loanItems, loansTotal,
      totalLiabilities,
      netWorth,
      liquidAssets, liquidPct,
      monthlySpend,
    };
  }, [accounts, creditCards, investments, budgetCategories, currentMonthKey]);

  // ═══════════════════════════ COMBINED TIMELINE ════════════════════════════
  const timeline = useMemo(() => {
    // Build from store balance_sheet_monthly data
    const historical = balanceSheetMonthly
      .filter((r) => r.month && r.month < currentMonthKey)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((r) => {
        const sub = computeSubtotals(r);
        return {
          month: r.month,
          totalAssets: sub.totalAssets,
          totalLiabilities: sub.totalLiabilities,
          netWorth: sub.netWorth,
        };
      });

    // Fill any gap months from net_worth_history that aren't covered by BS monthly
    const coveredMonths = new Set(historical.map((h) => h.month));
    const lastBSMonth = historical.length > 0 ? historical[historical.length - 1].month : '';
    const gapMap = new Map();
    netWorthHistory.forEach((entry) => {
      const entryMonth = (entry.date || '').slice(0, 7);
      if (entryMonth > lastBSMonth && entryMonth < currentMonthKey && !coveredMonths.has(entryMonth)) {
        const existing = gapMap.get(entryMonth);
        if (!existing || entry.date > existing.date) {
          gapMap.set(entryMonth, entry);
        }
      }
    });
    const gapEntries = [...gapMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, entry]) => ({
        month,
        totalAssets: entry.totalAssets || 0,
        totalLiabilities: entry.totalLiabilities || 0,
        netWorth: entry.netWorth || 0,
      }));

    const current = {
      month: currentMonthKey,
      totalAssets: liveBS.totalAssets,
      totalLiabilities: liveBS.totalLiabilities,
      netWorth: liveBS.netWorth,
      isCurrent: true,
    };

    return [...historical, ...gapEntries, current];
  }, [currentMonthKey, liveBS.totalAssets, liveBS.totalLiabilities, liveBS.netWorth, netWorthHistory, balanceSheetMonthly]);

  // ═══════════════════════════ KPI COMPUTATIONS ═════════════════════════════
  const kpis = useMemo(() => {
    const len = timeline.length;
    const curr = timeline[len - 1];
    const prev = len >= 2 ? timeline[len - 2] : null;
    const yearAgo = len >= 13 ? timeline[len - 13] : null;

    const momNWDollar = prev ? curr.netWorth - prev.netWorth : null;
    const momNWPct = prev && prev.netWorth
      ? ((curr.netWorth - prev.netWorth) / Math.abs(prev.netWorth)) * 100
      : null;
    const yoyNWDollar = yearAgo ? curr.netWorth - yearAgo.netWorth : null;
    const yoyNWPct = yearAgo && yearAgo.netWorth
      ? ((curr.netWorth - yearAgo.netWorth) / Math.abs(yearAgo.netWorth)) * 100
      : null;
    const momAssetDollar = prev ? curr.totalAssets - prev.totalAssets : null;
    const momAssetPct = prev && prev.totalAssets
      ? ((curr.totalAssets - prev.totalAssets) / Math.abs(prev.totalAssets)) * 100
      : null;

    const currentYear = new Date().getFullYear();
    const decPriorYear = `${currentYear - 1}-12`;
    const decPriorIdx = timeline.findIndex((t) => t.month === decPriorYear);
    const ytdBase = decPriorIdx >= 0 ? timeline[decPriorIdx] : null;
    const ytdPct = ytdBase && ytdBase.netWorth
      ? ((curr.netWorth - ytdBase.netWorth) / Math.abs(ytdBase.netWorth)) * 100
      : null;

    return {
      momNWDollar, momNWPct,
      yoyNWDollar, yoyNWPct,
      momAssetDollar, momAssetPct,
      ytdPct,
    };
  }, [timeline]);

  // ── Chart Data ──
  const chartData = useMemo(() => {
    return timeline.map((t) => ({
      label: formatMonthKey(t.month),
      netWorth: Math.round(t.netWorth),
      assets: Math.round(t.totalAssets),
      liabilities: Math.round(t.totalLiabilities),
    }));
  }, [timeline]);

  // ═══════════════════════════ COLUMN SELECTION ═════════════════════════════
  // Collapsed: last 12 months; Expanded: all months
  const { columns, allMonthData } = useMemo(() => {
    // Sort store months chronologically
    const storeMonths = balanceSheetMonthly
      .filter((r) => r.month)
      .sort((a, b) => a.month.localeCompare(b.month));
    const allHistoryMonths = storeMonths.map((r) => r.month);

    let startMonth;
    if (expanded) {
      startMonth = allHistoryMonths.length > 0 ? allHistoryMonths[0] : currentMonthKey;
    } else {
      const [curY, curM] = currentMonthKey.split('-').map(Number);
      const d = new Date(curY, curM - 1 - 11, 1); // 11 months back
      startMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }

    // Build display months from store data filtered by startMonth + current
    const displayMonths = allHistoryMonths.filter((m) => m >= startMonth);
    if (!displayMonths.includes(currentMonthKey)) {
      displayMonths.push(currentMonthKey);
    }

    // Build allMonthData map: compute subtotals from each store row
    const dataMap = {};
    storeMonths.forEach((row) => {
      const data = computeSubtotals(row);
      dataMap[row.month] = data;
    });

    // Live data for current month (include contribution data from store)
    const currentMonthBS = storeMonths.find((r) => r.month === currentMonthKey) || {};
    const liveData = buildLiveMonthData(liveBS, userSettings.creditScore || 0, currentMonthBS, accounts);
    dataMap[currentMonthKey] = liveData;

    // Build column objects
    const cols = displayMonths.map((month, i) => {
      const isLive = month === currentMonthKey;

      // Find the previous month for MoM calculations
      let prevKey = null;
      const histIdx = allHistoryMonths.indexOf(month);
      if (histIdx > 0) {
        prevKey = allHistoryMonths[histIdx - 1];
      } else if (month === currentMonthKey && allHistoryMonths.length > 0) {
        // For live month, prev is the last history month
        prevKey = allHistoryMonths[allHistoryMonths.length - 1];
      }

      return {
        key: month,
        label: isLive ? 'LIVE' : formatMonthKeyShort(month),
        isLive,
        prevKey,
      };
    });

    return { columns: cols, allMonthData: dataMap };
  }, [currentMonthKey, liveBS, expanded, balanceSheetMonthly, userSettings.creditScore]);

  /** Capture current computed values as a snapshot */
  const handleCapture = () => {
    // Use the same buildMonthSnapshot as auto-snapshot to stay consistent
    // with the pg_cron function (both use account balances, not holdings)
    const state = useStore.getState();
    const snapshot = buildMonthSnapshot(currentMonthKey, state);
    upsertBalanceSheetMonth(snapshot);

    // Write net_worth_history from the same snapshot to keep totals consistent
    const today = new Date().toLocaleDateString('sv-SE');
    addNetWorthSnapshot({
      date: today,
      totalAssets: snapshot.totalAssets,
      totalLiabilities: snapshot.totalLiabilities,
      netWorth: snapshot.netWorth,
    });
  };

  // ═══════════════════════════════ RENDER ════════════════════════════════════
  return (
    <div>
      <PageHeader title="Balance Sheet — Monthly" subtitle={currentMonthLabel}>
        <button
          onClick={handleCapture}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
          title="Capture current values as a monthly snapshot"
        >
          <Camera className="w-3.5 h-3.5" />
          Snapshot
        </button>
      </PageHeader>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 kpi-grid">
        <KPICard
          title="Net Worth"
          value={formatCurrency(liveBS.netWorth)}
          icon={Landmark}
        />
        <KPICard
          title="Total Assets"
          value={formatCurrency(liveBS.totalAssets)}
          icon={DollarSign}
        />
        <KPICard
          title="Total Liabilities"
          value={formatCurrency(liveBS.totalLiabilities)}
          icon={TrendingDown}
          variant="inverse"
        />
        <KPICard
          title="MoM $ Change"
          value={kpis.momNWDollar != null ? formatCurrency(kpis.momNWDollar) : '—'}
          change={kpis.momNWPct}
          icon={TrendingUp}
        />
        <KPICard
          title="MoM % Change"
          value={kpis.momNWPct != null ? formatPercent(kpis.momNWPct) : '—'}
          icon={TrendingUp}
        />
        <KPICard
          title="YTD % Change"
          value={kpis.ytdPct != null ? formatPercent(kpis.ytdPct) : '—'}
          icon={TrendingUp}
        />
      </div>

      {/* ── Net Worth Trend Chart ── */}
      <Card className="section-gap">
        <h3 className="text-sm font-semibold text-text-primary mb-4">
          Net Worth Trend
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="nwGradBS" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#30363D" />
              <XAxis
                dataKey="label"
                tick={{ fill: '#8B949E', fontSize: 11 }}
                axisLine={{ stroke: '#30363D' }}
                tickLine={false}
                interval={Math.max(0, Math.floor(chartData.length / 12) - 1)}
              />
              <YAxis
                tick={{ fill: '#8B949E', fontSize: 11 }}
                axisLine={{ stroke: '#30363D' }}
                tickLine={false}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<ChartTooltip />} />
              <Area
                type="monotone"
                dataKey="netWorth"
                name="Net Worth"
                stroke="#10B981"
                strokeWidth={2}
                fill="url(#nwGradBS)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* ═══════════════ HORIZONTAL MULTI-MONTH BALANCE SHEET ═══════════════ */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-primary">
            Balance Sheet
          </h3>
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
          >
            {expanded ? (
              <>
                <ChevronsRight className="w-3.5 h-3.5" />
                Last 12 Months
              </>
            ) : (
              <>
                <ChevronsLeft className="w-3.5 h-3.5" />
                Show All
              </>
            )}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table
            className="text-sm border-collapse"
            style={{ minWidth: `${270 + columns.length * 110}px` }}
          >
            <thead className="border-b border-border">
              <tr>
                <th
                  className="sticky left-0 z-20 text-left py-2 px-3 text-[11px] font-medium uppercase tracking-wider text-text-secondary whitespace-nowrap"
                  style={{ minWidth: '270px', backgroundColor: 'var(--color-bg-card)' }}
                >
                  Account
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`text-right py-2 px-2 text-[11px] font-medium uppercase tracking-wider whitespace-nowrap ${
                      col.isLive
                        ? 'text-accent bg-accent/8'
                        : 'text-text-secondary'
                    }`}
                    style={{ minWidth: '110px' }}
                  >
                    {col.isLive ? (
                      <span className="inline-flex items-center gap-1">
                        {formatMonthKeyShort(col.key)}
                        <span className="text-[9px] font-bold text-accent bg-accent/15 px-1 py-0.5 rounded">
                          LIVE
                        </span>
                      </span>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROW_DEFS.map((row) => (
                <BSRow
                  key={row.id}
                  row={row}
                  columns={columns}
                  allMonthData={allMonthData}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
